import React from 'react';
import PropTypes from 'prop-types';
import { graphql, createFragmentContainer } from 'react-relay';
import { connect } from 'react-redux';
import { injectIntl } from 'react-intl';

// components
import { SharedUserAndRelayVarLoader } from '../shared/SharedUserAndRelayVarLoader';
import { CurrencyProvider } from 'dibs-buyer-layout/exports/CurrencyProvider';
import { ServerVarsContextConsumer } from '../global/ServerVarsContext/ServerVarsContext';
import { PdpHead } from './PdpHead';
import { SharedTrackingContainer } from '../shared/SharedTrackingContainer';
import serverVars from 'server-vars';
import { CUSTOMER_TYPE_TRADE } from '../constants/userConstants';
import { BrowserHistoryContextProvider } from '../global/BrowserHistoryContext/BrowserHistoryContext';
import { getSkuTrackingParams } from './tracking/helpers/getSkuTrackingParams';
import { getPrivateOffer } from './helpers/getPrivateOffer';

// helpers
import get from 'lodash.get';
import { trackEcommerce } from './tracking/pdpTracking';
import { getLocalHistoryItemIds } from 'dibs-recent-history/exports/getLocalHistoryItemIds';
import { MAX_CAROUSEL_RECENTLY_VIEWED_ITEMS } from 'dibs-recent-history/exports/constants';

export class PdpSharedLayoutComponent extends React.Component {
    constructor(props) {
        super(props);

        this.getFollowSearchData = this.getFollowSearchData.bind(this);
        this.handleUserRefetch = this.handleUserRefetch.bind(this);
        this.trackingFunction = this.trackingFunction.bind(this);
        this.onSkuChange = this.onSkuChange.bind(this);
        this.getSkuWithId = this.getSkuWithId.bind(this);
        this.state = { skuId: null, numberOfSkuImages: null };
    }

    componentDidMount() {
        const privateOffer = this.isMultiSku()
            ? getPrivateOffer(get(this.props, 'viewer.transaction.edges'))
            : null;
        const privateOfferId = privateOffer && privateOffer.skuId ? privateOffer.skuId : null;
        const defaultSkuId = get(this.props, 'viewer.itemRead.0.defaultSkuId');
        // get sku id from url hash when component mounts
        const hash = window.location.hash || '';
        const skuIdFromUrl = get(hash.match(/skuId=(\w+)/), '1');
        const skuIdFromUrlIsValid = skuIdFromUrl && !!this.getSkuWithId(skuIdFromUrl);
        const initialSkuId = skuIdFromUrlIsValid ? skuIdFromUrl : privateOfferId || defaultSkuId;
        this.onSkuChange(initialSkuId);
    }

    componentDidUpdate(prevProps, prevState) {
        if (this.isMultiSku() && this.state.skuId !== prevState.skuId) {
            const queryParams = window.location.search || '';
            // update hash when sku changes
            window.history.replaceState(
                null,
                null,
                `${window.location.pathname}${queryParams}#skuId=${this.state.skuId}`
            );
        }
    }

    getSkuWithId(skuId) {
        const skus = get(this.props, 'viewer.itemRead.0.skus') || [];
        return skus.find(val => val.skuId === skuId);
    }

    getFollowSearchData() {
        const searchPage = get(this.props, 'viewer.itemRead.0.saveSearchData.page');
        return { followSearchPages: [searchPage], fetchFollowSearch: !!searchPage };
    }

    isMultiSku() {
        return !!get(this.props, 'viewer.itemRead.0.isMultiSku');
    }

    handleUserRefetch(variables, renderVariables, callback) {
        /**
         * Need to refetch sb items for trade users, the price depends on the `isTrade` variable
         * being set.
         *
         * Only do this after login, hence the server vars check, subsequent visits will depend on
         * the ut query param.
         */
        const fetchPdp = variables.isTrade && serverVars.get('userType') !== CUSTOMER_TYPE_TRADE;
        const localRecentHistoryItemIds = getLocalHistoryItemIds({ isClient: variables.isClient });

        const refetchVariables = {
            ...variables,
            ...this.getFollowSearchData(),
            fetchPdp,
            isMultiSku: this.isMultiSku(),
            localRecentHistoryItemIds,
            fetchUserRecentHistoryItem:
                localRecentHistoryItemIds.length < MAX_CAROUSEL_RECENTLY_VIEWED_ITEMS,
            isInteractivePdpChat: !!this.props?.viewer?.pdpChatFeatureFlag,
        };
        const updatedRenderVariables = {
            ...refetchVariables,
            ...renderVariables,
            fetchPdp: true,
        };
        this.props.doRefetch(refetchVariables, updatedRenderVariables, err => {
            callback(err, updatedRenderVariables);
        });
    }

    getTrackingData() {
        const params = get(this.props, 'viewer.itemRead.0.pdpTrackingVpvParams') || {};
        const { additionalTrackingData } = this.props;
        const skuParams = {
            skuType: this.isMultiSku() ? 'multi' : 'single',
            skuId: this.state.skuId,
            numberOfSkuImages: this.state.numberOfSkuImages,
            ...getSkuTrackingParams(params, this.state.skuId),
        };

        return {
            ...params,
            ...additionalTrackingData,
            ...skuParams,
            hasViewInRoom: get(this.props, 'viewer.itemRead.0.viewInRoomPhotos', []).length,
        };
    }

    trackingFunction() {
        const item = get(this.props, 'viewer.itemRead.0');
        trackEcommerce(item, this.state.skuId);
    }

    onSkuChange(skuId) {
        if (skuId !== this.state.skuId) {
            const sku = this.getSkuWithId(skuId);
            const numberOfSkuImages = get(sku, 'images.length');

            this.setState({
                skuId,
                numberOfSkuImages,
            });
        }
    }

    render() {
        const { user, refreshPageOnTradeLogin } = this.props;
        const { url: opt_pageURL, ...additional } = this.getTrackingData() || {}; // eslint-disable-line camelcase
        const gtmPageInfo = { opt_pageURL }; // eslint-disable-line camelcase
        const { fetchFollowSearch } = this.getFollowSearchData();
        const onSkuChange = this.onSkuChange;

        return (
            <BrowserHistoryContextProvider>
                <ServerVarsContextConsumer>
                    {({ clientConf, isRemoveAmpPdpVariant }) => {
                        const pdpHead = new PdpHead({
                            ...this.props,
                            clientConf,
                            isRemoveAmpPdpVariant,
                        });
                        return (
                            <React.Fragment>
                                <SharedUserAndRelayVarLoader
                                    refreshPageOnTradeLogin={refreshPageOnTradeLogin}
                                    onUserRefetch={this.handleUserRefetch}
                                />
                                <CurrencyProvider
                                    render={({ currency, showCurrency }) => {
                                        return (
                                            <React.Fragment>
                                                <SharedTrackingContainer
                                                    currency={currency}
                                                    user={user}
                                                    gtmPageInfo={gtmPageInfo}
                                                    onTrackingFired={this.trackingFunction}
                                                    additional={additional}
                                                    skuId={this.state.skuId}
                                                    isMultiSku={this.isMultiSku()}
                                                />
                                                {this.props.children({
                                                    pdpHead,
                                                    currency,
                                                    showCurrency,
                                                    fetchFollowSearch,
                                                    selectedSkuId: this.state.skuId,
                                                    onSkuChange,
                                                })}
                                            </React.Fragment>
                                        );
                                    }}
                                />
                            </React.Fragment>
                        );
                    }}
                </ServerVarsContextConsumer>
            </BrowserHistoryContextProvider>
        );
    }
}

PdpSharedLayoutComponent.defaultProps = {
    additionalTrackingData: {},
};

PdpSharedLayoutComponent.propTypes = {
    children: PropTypes.func.isRequired,
    additionalTrackingData: PropTypes.object,
    doRefetch: PropTypes.func.isRequired,
    viewer: PropTypes.object.isRequired,
    user: PropTypes.object,
    intl: PropTypes.object.isRequired,
    refreshPageOnTradeLogin: PropTypes.bool,
};

function mapStateToProps({ intlVariables }) {
    return { intlVariables };
}

export const PdpSharedLayout = injectIntl(
    createFragmentContainer(connect(mapStateToProps)(PdpSharedLayoutComponent), {
        viewer: graphql`
            fragment PdpSharedLayout_viewer on Viewer {
                pdpChatFeatureFlag: featureFlag(feature: "pdpChat")
                ...PdpHead_viewer @relay(mask: false)
                itemRead(itemIds: $itemIds) {
                    ... on Item {
                        serviceId
                        isMultiSku
                        defaultSkuId
                        pdpTrackingVpvParams(userId: $userId) @include(if: $isClient)
                        saveSearchData {
                            page
                        }
                        ... @include(if: $fetchPdp) {
                            ...PdpHead_item @relay(mask: false)
                            pdpMeta {
                                title
                            }
                            attributes {
                                period {
                                    option {
                                        name
                                    }
                                }
                            }
                            browseUrl
                            categoryCode
                            seller {
                                serviceId
                                isInternal
                            }
                            viewInRoomPhotos {
                                src
                            }
                            contemporaryTrackingString
                        }
                        skus {
                            skuId
                            images {
                                id
                            }
                        }
                    }
                }
                transactions(itemId: $itemIds, buyerId: $userIds) @include(if: $isClient) {
                    edges @include(if: $isMultiSku) {
                        node {
                            skuId
                            isPrivateOffer
                            isCanceled
                        }
                    }
                }
            }
        `,
        user: graphql`
            fragment PdpSharedLayout_user on User {
                ...SharedTrackingContainer_user
            }
        `,
    })
);
